import React from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  ClockIcon,
  UserGroupIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';

const valueProps = [
  {
    icon: ChartBarIcon,
    title: 'Mastery Data in Real Time',
    description: "Every student's progress against curriculum outcomes — visible instantly.",
    color: '#0ea5e9',
  },
  {
    icon: UserGroupIcon,
    title: 'Targeted Differentiation',
    description: 'See who needs intervention, who is ready to extend, and where the class is struggling.',
    color: '#8b5cf6',
  },
  {
    icon: ClockIcon,
    title: 'Practice Handled Outside Class',
    description: 'Adaptive scaffolding and feedback happen at home — so contact time goes further.',
    color: '#10b981',
  },
  {
    icon: AcademicCapIcon,
    title: 'More Time for What Matters',
    description: 'Rich discussion, relationship-building, and the complex teaching that only you can do.',
    color: '#f59e0b',
  },
];

const TeacherSlideValueProp: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center h-full px-16 relative overflow-hidden">
      {/* Background radial glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(14,165,233,0.07) 0%, transparent 70%)',
        }}
      />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-6 relative z-10"
      >
        <h2 className="text-5xl font-bold text-white mb-3">
          What This Means{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] to-[#06b6d4]">
            for Teachers
          </span>
        </h2>
        <p className="text-lg text-[#94a3b8] max-w-2xl mx-auto">
          Clearer data, less busywork, more time for the teaching that matters.
        </p>
      </motion.div>

      {/* Value Prop Cards — 2x2 Grid */}
      <div className="grid grid-cols-2 gap-5 max-w-5xl w-full relative z-10 mt-4">
        {valueProps.map((prop, index) => {
          const Icon = prop.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.12 }}
              className="relative group"
            >
              {/* Hover glow */}
              <div
                className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"
                style={{
                  background: `linear-gradient(135deg, ${prop.color}30, transparent 60%)`,
                }}
              />

              {/* Card */}
              <div
                className="relative rounded-2xl p-6 h-full"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                  border: `1px solid ${prop.color}25`,
                }}
              >
                <div className="flex items-start gap-5">
                  {/* Icon */}
                  <div
                    className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{
                      background: `${prop.color}15`,
                      border: `1px solid ${prop.color}30`,
                    }}
                  >
                    <Icon className="w-6 h-6" style={{ color: prop.color }} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-white mb-1.5">{prop.title}</h3>
                    <p className="text-[#94a3b8] leading-relaxed text-[15px]">
                      {prop.description}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Closing line */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.0 }}
        className="mt-8 text-lg text-[#94a3b8] relative z-10"
      >
        Built to support teachers, not replace them.
      </motion.p>
    </div>
  );
};

export default TeacherSlideValueProp;
